import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { renderAllowedHtml } from '../../../shared/allowedHtml';
import { Translate, TranslatePlain } from '../../../shared/LanguageService';
import { useNotificationPreviews } from '../state/useNotificationPreviews';
import { NotificationTranslation } from '../types';

const alertSeverity = {
    success: 'success',
    failure: 'error',
    warning: 'warning',
    info: 'info'
} as const;

function groupedByFeature(entries: NotificationTranslation[]) {
    const groups: Array<{ feature: string; entries: NotificationTranslation[] }> = [];
    for (const entry of entries) {
        const last = groups[groups.length - 1];
        if (last && last.feature === entry.feature) {
            last.entries.push(entry);
        } else {
            groups.push({ feature: entry.feature, entries: [entry] });
        }
    }
    return groups;
}

function NotificationPreviews({ location }: RouteComponentProps) {
    const { entries, scopedToRoute, canScopeToRoute, showAll, setShowAll } =
        useNotificationPreviews(location.pathname);

    return (
        <Paper
            elevation={6}
            data-testid="notification-previews"
            sx={{
                position: 'fixed',
                left: 16,
                bottom: 16,
                width: 420,
                maxHeight: '70vh',
                overflowY: 'auto',
                padding: 2,
                zIndex: (theme) => theme.zIndex.modal + 1
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">
                    {TranslatePlain(
                        scopedToRoute
                            ? 'translation-editor/notifications/for-this-page'
                            : 'translation-editor/toggle/show-notifications'
                    )}
                </Typography>
                {canScopeToRoute ? (
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showAll}
                                onChange={(event, checked) => setShowAll(checked)}
                            />
                        }
                        label={TranslatePlain('translation-editor/notifications/show-all')}
                    />
                ) : null}
            </Box>
            {groupedByFeature(entries).map((group) => (
                <Box key={group.feature} sx={{ marginBottom: 2 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                    >
                        {group.feature}
                    </Typography>
                    {group.entries.map((entry) => (
                        <Alert
                            key={`${entry.key}:${entry.severity}`}
                            severity={alertSeverity[entry.severity]}
                            sx={{ marginTop: 1, fontSize: '16px' }}
                        >
                            {renderAllowedHtml(Translate(entry.key))}
                        </Alert>
                    ))}
                </Box>
            ))}
        </Paper>
    );
}

export default withRouter(NotificationPreviews);
